<script setup lang="ts">
import { Dayjs } from 'dayjs'
import { computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { AutomationGroup, AutomationGroupStat, AutomationTypeEnum } from '@/store/modules/automation/types'

import AutomationGroupThumbnail from './AutomationGroupThumbnail.vue'
const props = defineProps<{
  isAutomationAllowed: boolean
  isAutomationEnabled: boolean
  earlyLimit: Dayjs
  lateLimit: Dayjs
}>()

const { t } = useI18n()

const store = useStore()
const getAutomationGroupsStatsRequest = computed(() => store.state.automation.getAutomationGroupsStatsRequest)
const automationGroupStats = computed<AutomationGroupStat[]>(() => store.state.automation.automationGroupStats)
const automationGroups = computed<AutomationGroup[]>(() => store.state.automation.automationGroups)
const automationGroupsGhmCollisions = computed(() => store.state.automation.automationGroupsGhmCollisions)
const flatSancareSettings = computed(() => store.getters.flatSancareSettings)
const canAddRulesGroup = computed<boolean>(() => store.getters.flatSancareSettings.rules_automation)
const rulesGroups = computed<AutomationGroup[]>(() => automationGroups.value[AutomationTypeEnum.RULES] ?? [])
onMounted(() => {
  if (!props.isAutomationAllowed || !props.isAutomationEnabled) {
    return
  }

  store.commit('setStayListParam', { type: 'currentPage', value: 1 })
})

watch([automationGroups, props.earlyLimit, props.lateLimit], () => refreshStats())

function removeAutomationGroup(id: number) {
  store.dispatch('removeAutomationGroup', id)
}

function getGroupStats(id: number) {
  return automationGroupStats.value.find((g) => g.id === id)
}

function getGhmCollidingGroupNames(id: number) {
  return automationGroupsGhmCollisions.value.find((g) => g.id === id)?.collidingGroups || []
}

function refreshStats() {
  store.dispatch('getAutomationGroupsStats', {
    earlyLimit: props.earlyLimit,
    lateLimit: props.lateLimit
  })
}

function getDemoMode() {
  return flatSancareSettings.value.demo_mode
}

function sancareSettings(key: string) {
  const setting = flatSancareSettings.value[key]
  const demoMode = getDemoMode()

  const demo = demoMode === true ?  ` (${t('settings.withInitialCodage')})` : ''
  switch (setting) {
    case true:
      return `${t('settings.enabled')}${demo}`
    case false:
      return t('settings.disabled')
    default:
      return setting
  }
}
</script>

<template>
  <div v-if="getAutomationGroupsStatsRequest.ok">
    <div class="home-list-part border-top-0">
      <div class="row g-0 justify-content-between p-3">
        <h2 class="home-list-part-title col-12 col-md-7 text-center text-md-start">
          {{ t('automation.titleAi') }}
        </h2>
        <router-link
          class="btn btn-link col-12 col-md-auto text-enter text-md-end"
          :to="'/automation/edit-automation-group?mode=automation&automationType=ai'"
        >
          +&nbsp;{{ t('automation.actionsAi.create') }}
        </router-link>
      </div>

      <div
        class="col my-3"
      >
        <div class="alert alert-secondary">
          {{ t('automation.mode.title' ) }} :
          <ul class="m-0">
            <li>{{ t('automation.session') }} : <strong>{{ sancareSettings('session_automation') }}</strong></li>
            <li> {{ t('automation.nonSession') }} : <strong>{{ sancareSettings('non_session_automation') }}</strong></li>
          </ul>
        </div>
      </div>

      <div class="row g-0">
        <div
          v-for="automationGroup in automationGroups[AutomationTypeEnum.AI]"
          :key="automationGroup.id"
          class="col-4 px-3"
        >
          <AutomationGroupThumbnail
            :early-limit="earlyLimit"
            :late-limit="lateLimit"
            :stats-group="getGroupStats(automationGroup.id)"
            :full-group="automationGroup"
            :ghm-colliding-group-names="getGhmCollidingGroupNames(automationGroup.id)"
            :demo-mode="getDemoMode()"
            @remove-automation-group="removeAutomationGroup"
          />
        </div>
      </div>
    </div>

    <div
      v-if="canAddRulesGroup"
      class="home-list-part"
    >
      <div class="row g-0 justify-content-between p-3">
        <h2 class="home-list-part-title text-center text-md-start col-12 col-md-6">
          {{ t('automation.titleRule') }}
        </h2>
        <router-link
          class="btn btn-link text-center text-md-end col-12 col-md-6"
          :to="'/automation/edit-automation-group?mode=automation&automationType=rules'"
        >
          +&nbsp;{{ t('automation.actionsRule.create') }}
        </router-link>
      </div>

      <div class="row g-0">
        <div
          v-for="automationGroup in rulesGroups"
          :key="automationGroup.id"
          class="col-4 px-3"
        >
          <AutomationGroupThumbnail
            :early-limit="earlyLimit"
            :late-limit="lateLimit"
            :stats-group="getGroupStats(automationGroup.id)"
            :full-group="automationGroup"
            :ghm-colliding-group-names="getGhmCollidingGroupNames(automationGroup.id)"
            :demo-mode="getDemoMode()"
            @remove-automation-group="removeAutomationGroup"
          />
        </div>
      </div>
    </div>
  </div>
</template>
